/**
 * Hala CRM - Users Management Page
 * Manage CRM users with roles and group assignments
 */

import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { 
  Users, UserPlus, Search, Filter, Edit2, Trash2, Check, X,
  Shield, UserCheck, Eye, MoreVertical, Mail, Phone, RefreshCw
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('hala_admin_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const COLORS = {
  primary: '#1B3A6B',
  primaryLight: '#2A4A7A',
  gold: '#C5A572',
  success: '#10B981',
  successLight: '#D1FAE5',
  warning: '#F59E0B',
  warningLight: '#FEF3C7',
  error: '#EF4444',
  errorLight: '#FEE2E2',
  white: '#FFFFFF',
  bg: '#F8FAFC',
  bgDark: '#F1F5F9',
  border: '#E2E8F0',
  text: '#1E293B',
  textMuted: '#64748B',
};

const ROLE_COLORS = {
  admin: { bg: '#FEE2E2', text: '#DC2626', label: 'مدير النظام' },
  supervisor: { bg: '#DBEAFE', text: '#2563EB', label: 'مشرف' },
  consultant: { bg: '#D1FAE5', text: '#059669', label: 'مستشار' },
  group_user: { bg: '#FEF3C7', text: '#D97706', label: 'مستخدم مجموعة' },
};

const STATUS_COLORS = {
  active: { bg: COLORS.successLight, text: COLORS.success, label: 'نشط' },
  inactive: { bg: COLORS.errorLight, text: COLORS.error, label: 'غير نشط' },
};

function UserModal({ user, roles, programs, groups, onSave, onClose }) {
  const [formData, setFormData] = useState({
    name_ar: user?.name_ar || '',
    name_en: user?.name_en || '',
    email: user?.email || '',
    mobile: user?.mobile || '',
    role_id: user?.role_id || 'group_user',
    assigned_programs: user?.assigned_programs || [],
    assigned_groups: user?.assigned_groups || [],
    status: user?.status || 'active',
    notes: user?.notes || '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      await onSave(formData);
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'حدث خطأ أثناء الحفظ');
    } finally {
      setLoading(false);
    }
  };

  const toggleProgram = (program) => {
    setFormData(prev => ({
      ...prev,
      assigned_programs: prev.assigned_programs.includes(program)
        ? prev.assigned_programs.filter(p => p !== program)
        : [...prev.assigned_programs, program]
    }));
  };

  const toggleGroup = (groupId) => {
    setFormData(prev => ({
      ...prev,
      assigned_groups: prev.assigned_groups.includes(groupId)
        ? prev.assigned_groups.filter(g => g !== groupId)
        : [...prev.assigned_groups, groupId]
    }));
  };

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px',
    }}>
      <div style={{
        backgroundColor: COLORS.white,
        borderRadius: '16px',
        width: '100%',
        maxWidth: '600px',
        maxHeight: '90vh',
        overflow: 'auto',
      }}>
        <div style={{
          padding: '20px 24px',
          borderBottom: `1px solid ${COLORS.border}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: COLORS.text }}>
            {user ? 'تعديل المستخدم' : 'إضافة مستخدم جديد'}
          </h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
            <X size={20} color={COLORS.textMuted} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: '24px' }}>
          {error && (
            <div style={{
              padding: '12px 16px',
              backgroundColor: COLORS.errorLight,
              color: COLORS.error,
              borderRadius: '8px',
              marginBottom: '16px',
              fontSize: '14px',
            }}>
              {error}
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 500, color: COLORS.text }}>
                الاسم بالعربي *
              </label>
              <input
                type="text"
                required
                value={formData.name_ar}
                onChange={(e) => setFormData({ ...formData, name_ar: e.target.value })}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: '8px',
                  fontSize: '14px',
                  outline: 'none',
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 500, color: COLORS.text }}>
                الاسم بالإنجليزي
              </label>
              <input
                type="text"
                value={formData.name_en}
                onChange={(e) => setFormData({ ...formData, name_en: e.target.value })}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: '8px',
                  fontSize: '14px',
                  outline: 'none',
                }}
              />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 500, color: COLORS.text }}>
                البريد الإلكتروني *
              </label>
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: '8px',
                  fontSize: '14px',
                  outline: 'none',
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 500, color: COLORS.text }}>
                رقم الجوال
              </label>
              <input
                type="tel"
                value={formData.mobile}
                onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: '8px',
                  fontSize: '14px',
                  outline: 'none',
                }}
              />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 500, color: COLORS.text }}>
                الدور *
              </label>
              <select
                required
                value={formData.role_id}
                onChange={(e) => setFormData({ ...formData, role_id: e.target.value })}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: '8px',
                  fontSize: '14px',
                  outline: 'none',
                  backgroundColor: COLORS.white,
                }}
              >
                {roles.map(role => (
                  <option key={role.role_id} value={role.role_id}>
                    {role.role_name_ar}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 500, color: COLORS.text }}>
                الحالة
              </label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: '8px',
                  fontSize: '14px',
                  outline: 'none',
                  backgroundColor: COLORS.white,
                }}
              >
                <option value="active">نشط</option>
                <option value="inactive">غير نشط</option>
              </select>
            </div>
          </div>

          {formData.role_id !== 'admin' && (
            <>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: 500, color: COLORS.text }}>
                  البرامج المسندة
                </label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {programs.map(prog => (
                    <button
                      key={prog.program_name_ar}
                      type="button"
                      onClick={() => toggleProgram(prog.program_name_ar)}
                      style={{
                        padding: '6px 14px',
                        borderRadius: '20px',
                        border: `1px solid ${formData.assigned_programs.includes(prog.program_name_ar) ? COLORS.primary : COLORS.border}`,
                        backgroundColor: formData.assigned_programs.includes(prog.program_name_ar) ? COLORS.primary : COLORS.white,
                        color: formData.assigned_programs.includes(prog.program_name_ar) ? COLORS.white : COLORS.text,
                        fontSize: '13px',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                      }}
                    >
                      {prog.program_name_ar}
                    </button>
                  ))}
                </div>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: 500, color: COLORS.text }}>
                  المجموعات المسندة
                </label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {groups.map(grp => (
                    <button
                      key={grp.group_id}
                      type="button"
                      onClick={() => toggleGroup(grp.group_id)}
                      style={{
                        padding: '6px 14px',
                        borderRadius: '20px',
                        border: `1px solid ${formData.assigned_groups.includes(grp.group_id) ? COLORS.gold : COLORS.border}`,
                        backgroundColor: formData.assigned_groups.includes(grp.group_id) ? COLORS.gold : COLORS.white,
                        color: formData.assigned_groups.includes(grp.group_id) ? COLORS.white : COLORS.text,
                        fontSize: '13px',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                      }}
                    >
                      {grp.group_name_ar}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 500, color: COLORS.text }}>
              ملاحظات
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
              style={{
                width: '100%',
                padding: '10px 14px',
                border: `1px solid ${COLORS.border}`,
                borderRadius: '8px',
                fontSize: '14px',
                outline: 'none',
                resize: 'vertical',
              }}
            />
          </div>

          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: '10px 24px',
                border: `1px solid ${COLORS.border}`,
                borderRadius: '8px',
                backgroundColor: COLORS.white,
                color: COLORS.text,
                fontSize: '14px',
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              إلغاء
            </button>
            <button
              type="submit"
              disabled={loading}
              style={{
                padding: '10px 24px',
                border: 'none',
                borderRadius: '8px',
                backgroundColor: COLORS.primary,
                color: COLORS.white,
                fontSize: '14px',
                fontWeight: 500,
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.7 : 1,
              }}
            >
              {loading ? 'جاري الحفظ...' : 'حفظ'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function CRMUsersPage() {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [stats, setStats] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [usersRes, rolesRes, programsRes, groupsRes, statsRes] = await Promise.all([
        axios.get(`${API}/crm/users`, { 
          headers: getAuthHeaders(),
          params: {
            search: searchQuery || undefined,
            role: roleFilter || undefined,
            status: statusFilter || undefined,
          }
        }),
        axios.get(`${API}/crm/roles`, { headers: getAuthHeaders() }),
        axios.get(`${API}/crm/programs`, { headers: getAuthHeaders() }),
        axios.get(`${API}/crm/groups`, { headers: getAuthHeaders() }),
        axios.get(`${API}/crm/stats`, { headers: getAuthHeaders() }),
      ]);
      
      setUsers(usersRes.data.items || []);
      setRoles(rolesRes.data || []);
      setPrograms(programsRes.data || []);
      setGroups(groupsRes.data || []);
      setStats(statsRes.data);
    } catch (e) {
      console.error('Failed to fetch data:', e);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, roleFilter, statusFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSaveUser = async (formData) => {
    if (editingUser) {
      await axios.put(`${API}/crm/users/${editingUser.user_id}`, formData, { headers: getAuthHeaders() });
    } else {
      await axios.post(`${API}/crm/users`, formData, { headers: getAuthHeaders() });
    }
    fetchData();
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا المستخدم؟')) return;
    
    try {
      await axios.delete(`${API}/crm/users/${userId}`, { headers: getAuthHeaders() });
      fetchData();
    } catch (e) {
      console.error('Failed to delete user:', e);
    }
  };

  const openEditModal = (user) => {
    setEditingUser(user);
    setShowModal(true);
  };

  const openAddModal = () => {
    setEditingUser(null);
    setShowModal(true);
  };

  return (
    <div style={{ padding: '0' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <div>
            <h1 style={{ margin: '0 0 8px', fontSize: '24px', fontWeight: 700, color: COLORS.text }}>
              إدارة المستخدمين
            </h1>
            <p style={{ margin: 0, fontSize: '14px', color: COLORS.textMuted }}>
              إدارة مستخدمي النظام وصلاحياتهم ومجموعاتهم
            </p>
          </div>
          <button
            onClick={openAddModal}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 20px',
              backgroundColor: COLORS.primary,
              color: COLORS.white,
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            <UserPlus size={18} />
            إضافة مستخدم
          </button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px' }}>
            <div style={{ backgroundColor: COLORS.white, padding: '20px', borderRadius: '12px', border: `1px solid ${COLORS.border}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ width: '40px', height: '40px', borderRadius: '10px', backgroundColor: `${COLORS.primary}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Users size={20} color={COLORS.primary} />
                </div>
                <div>
                  <p style={{ margin: 0, fontSize: '12px', color: COLORS.textMuted }}>إجمالي المستخدمين</p>
                  <p style={{ margin: 0, fontSize: '24px', fontWeight: 700, color: COLORS.text }}>{stats.users?.total || 0}</p>
                </div>
              </div>
            </div>
            <div style={{ backgroundColor: COLORS.white, padding: '20px', borderRadius: '12px', border: `1px solid ${COLORS.border}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ width: '40px', height: '40px', borderRadius: '10px', backgroundColor: `${COLORS.success}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <UserCheck size={20} color={COLORS.success} />
                </div>
                <div>
                  <p style={{ margin: 0, fontSize: '12px', color: COLORS.textMuted }}>نشطون</p>
                  <p style={{ margin: 0, fontSize: '24px', fontWeight: 700, color: COLORS.success }}>{stats.users?.active || 0}</p>
                </div>
              </div>
            </div>
            <div style={{ backgroundColor: COLORS.white, padding: '20px', borderRadius: '12px', border: `1px solid ${COLORS.border}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ width: '40px', height: '40px', borderRadius: '10px', backgroundColor: `${COLORS.gold}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Shield size={20} color={COLORS.gold} />
                </div>
                <div>
                  <p style={{ margin: 0, fontSize: '12px', color: COLORS.textMuted }}>مشرفون</p>
                  <p style={{ margin: 0, fontSize: '24px', fontWeight: 700, color: COLORS.gold }}>{stats.users?.by_role?.supervisor || 0}</p>
                </div>
              </div>
            </div>
            <div style={{ backgroundColor: COLORS.white, padding: '20px', borderRadius: '12px', border: `1px solid ${COLORS.border}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ width: '40px', height: '40px', borderRadius: '10px', backgroundColor: `${COLORS.warning}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <UserCheck size={20} color={COLORS.warning} />
                </div>
                <div>
                  <p style={{ margin: 0, fontSize: '12px', color: COLORS.textMuted }}>مستشارون</p>
                  <p style={{ margin: 0, fontSize: '24px', fontWeight: 700, color: COLORS.warning }}>{stats.users?.by_role?.consultant || 0}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '16px' }}>
          <div style={{ flex: 1, position: 'relative' }}>
            <Search size={18} color={COLORS.textMuted} style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)' }} />
            <input
              type="text"
              placeholder="بحث بالاسم أو البريد..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 40px 10px 14px',
                border: `1px solid ${COLORS.border}`,
                borderRadius: '8px',
                fontSize: '14px',
                outline: 'none',
              }}
            />
          </div>
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            style={{
              padding: '10px 14px',
              border: `1px solid ${COLORS.border}`,
              borderRadius: '8px',
              fontSize: '14px',
              outline: 'none',
              backgroundColor: COLORS.white,
              minWidth: '140px',
            }}
          >
            <option value="">كل الأدوار</option>
            {roles.map(role => (
              <option key={role.role_id} value={role.role_id}>{role.role_name_ar}</option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{
              padding: '10px 14px',
              border: `1px solid ${COLORS.border}`,
              borderRadius: '8px',
              fontSize: '14px',
              outline: 'none',
              backgroundColor: COLORS.white,
              minWidth: '120px',
            }}
          >
            <option value="">كل الحالات</option>
            <option value="active">نشط</option>
            <option value="inactive">غير نشط</option>
          </select>
          <button
            onClick={fetchData}
            style={{
              padding: '10px',
              border: `1px solid ${COLORS.border}`,
              borderRadius: '8px',
              backgroundColor: COLORS.white,
              cursor: 'pointer',
            }}
          >
            <RefreshCw size={18} color={COLORS.textMuted} />
          </button>
        </div>
      </div>

      {/* Users Table */}
      <div style={{ backgroundColor: COLORS.white, borderRadius: '12px', border: `1px solid ${COLORS.border}`, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: '60px', textAlign: 'center', color: COLORS.textMuted }}>
            جاري التحميل...
          </div>
        ) : users.length === 0 ? (
          <div style={{ padding: '60px', textAlign: 'center', color: COLORS.textMuted }}>
            <Users size={48} style={{ marginBottom: '16px', opacity: 0.4 }} />
            <p>لا يوجد مستخدمون</p>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: COLORS.bg }}>
                <th style={{ padding: '14px 16px', textAlign: 'right', fontSize: '13px', fontWeight: 600, color: COLORS.textMuted, borderBottom: `1px solid ${COLORS.border}` }}>المستخدم</th>
                <th style={{ padding: '14px 16px', textAlign: 'right', fontSize: '13px', fontWeight: 600, color: COLORS.textMuted, borderBottom: `1px solid ${COLORS.border}` }}>الدور</th>
                <th style={{ padding: '14px 16px', textAlign: 'right', fontSize: '13px', fontWeight: 600, color: COLORS.textMuted, borderBottom: `1px solid ${COLORS.border}` }}>البرامج</th>
                <th style={{ padding: '14px 16px', textAlign: 'right', fontSize: '13px', fontWeight: 600, color: COLORS.textMuted, borderBottom: `1px solid ${COLORS.border}` }}>المجموعات</th>
                <th style={{ padding: '14px 16px', textAlign: 'right', fontSize: '13px', fontWeight: 600, color: COLORS.textMuted, borderBottom: `1px solid ${COLORS.border}` }}>الحالة</th>
                <th style={{ padding: '14px 16px', textAlign: 'center', fontSize: '13px', fontWeight: 600, color: COLORS.textMuted, borderBottom: `1px solid ${COLORS.border}` }}>إجراءات</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => {
                const roleStyle = ROLE_COLORS[user.role_id] || { bg: COLORS.bgDark, text: COLORS.textMuted, label: user.role_id };
                const statusStyle = STATUS_COLORS[user.status] || STATUS_COLORS.inactive;
                
                return (
                  <tr key={user.user_id} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                    <td style={{ padding: '14px 16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '10px',
                          backgroundColor: COLORS.primary,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: COLORS.white,
                          fontWeight: 600,
                          fontSize: '14px',
                        }}>
                          {user.name_ar?.charAt(0) || 'م'}
                        </div>
                        <div>
                          <p style={{ margin: 0, fontSize: '14px', fontWeight: 500, color: COLORS.text }}>{user.name_ar}</p>
                          <p style={{ margin: '2px 0 0', fontSize: '12px', color: COLORS.textMuted, display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <Mail size={12} /> {user.email}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '14px 16px' }}>
                      <span style={{
                        padding: '4px 12px',
                        borderRadius: '20px',
                        backgroundColor: roleStyle.bg,
                        color: roleStyle.text,
                        fontSize: '12px',
                        fontWeight: 500,
                      }}>
                        {roleStyle.label}
                      </span>
                    </td>
                    <td style={{ padding: '14px 16px' }}>
                      <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                        {user.assigned_programs?.length > 0 ? (
                          user.assigned_programs.slice(0, 2).map((prog, i) => (
                            <span key={i} style={{
                              padding: '2px 8px',
                              borderRadius: '4px',
                              backgroundColor: COLORS.bgDark,
                              color: COLORS.text,
                              fontSize: '11px',
                            }}>
                              {prog}
                            </span>
                          ))
                        ) : (
                          <span style={{ fontSize: '12px', color: COLORS.textMuted }}>-</span>
                        )}
                        {user.assigned_programs?.length > 2 && (
                          <span style={{ fontSize: '11px', color: COLORS.textMuted }}>+{user.assigned_programs.length - 2}</span>
                        )}
                      </div>
                    </td>
                    <td style={{ padding: '14px 16px' }}>
                      <span style={{ fontSize: '13px', color: COLORS.text }}>
                        {user.assigned_groups?.length || 0} مجموعة
                      </span>
                    </td>
                    <td style={{ padding: '14px 16px' }}>
                      <span style={{
                        padding: '4px 12px',
                        borderRadius: '20px',
                        backgroundColor: statusStyle.bg,
                        color: statusStyle.text,
                        fontSize: '12px',
                        fontWeight: 500,
                      }}>
                        {statusStyle.label}
                      </span>
                    </td>
                    <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                        <button
                          onClick={() => openEditModal(user)}
                          style={{
                            padding: '6px',
                            border: 'none',
                            borderRadius: '6px',
                            backgroundColor: COLORS.bgDark,
                            cursor: 'pointer',
                          }}
                          title="تعديل"
                        >
                          <Edit2 size={16} color={COLORS.primary} />
                        </button>
                        <button
                          onClick={() => handleDeleteUser(user.user_id)}
                          style={{
                            padding: '6px',
                            border: 'none',
                            borderRadius: '6px',
                            backgroundColor: COLORS.errorLight,
                            cursor: 'pointer',
                          }}
                          title="حذف"
                        >
                          <Trash2 size={16} color={COLORS.error} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <UserModal
          user={editingUser}
          roles={roles}
          programs={programs}
          groups={groups}
          onSave={handleSaveUser}
          onClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
}
